import { profileSpaceCreatorKindSchema, profileSpaceHistoryGatewayHostSchema } from "./profile-space-history.js";
import { spaceOrganizationVisibilitySchema } from "./space-meta.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/account-space-index.ts
const ISO_DATE_TIME_SCHEMA = z$1.iso.datetime({ offset: true });
const trimmedNonEmptyStringSchema = z$1.string().trim().min(1);
const nullableTrimmedStringSchema = z$1.union([trimmedNonEmptyStringSchema, z$1.null()]);
const nullableNonNegativeIntSchema = z$1.union([z$1.number().int().min(0), z$1.null()]);
const nullableIsoDateTimeSchema = z$1.union([ISO_DATE_TIME_SCHEMA, z$1.null()]);
const ACCOUNT_SPACE_INDEX_MAX_LIMIT = 500;
const NON_NEGATIVE_INT_RE = /^\d+$/;
const accountSpaceIndexScopeSchema = z$1.enum(["account", "profile"]);
const accountSpaceIndexLimitSchema = z$1.preprocess((value) => {
	if (typeof value === "number") return value;
	if (typeof value !== "string") return value;
	const normalized = value.trim();
	if (!NON_NEGATIVE_INT_RE.test(normalized)) return NaN;
	return Number.parseInt(normalized, 10);
}, z$1.number().int().min(1).max(ACCOUNT_SPACE_INDEX_MAX_LIMIT)).optional();
const accountSpaceIndexIncludeRecentlyDeletedItemsSchema = z$1.preprocess((value) => {
	if (typeof value === "boolean") return value;
	return value === "true";
}, z$1.boolean()).optional();
const accountSpaceIndexIncludePurposesSchema = z$1.preprocess((value) => {
	if (typeof value === "boolean") return value;
	return value === "true";
}, z$1.boolean()).optional();
const accountSpaceIndexIncludeDirectMessageProfileIdsSchema = z$1.preprocess((value) => {
	if (typeof value === "boolean") return value;
	return value === "true";
}, z$1.boolean()).optional();
const accountSpaceIndexQuerySchema = z$1.strictObject({
	includeMessagePreviews: z$1.preprocess((value) => value === true || value === "true", z$1.boolean()).optional(),
	includeMemberPreviews: z$1.preprocess((value) => value === true || value === "true", z$1.boolean()).optional(),
	gatewayHost: profileSpaceHistoryGatewayHostSchema,
	includeDirectMessageProfileIds: accountSpaceIndexIncludeDirectMessageProfileIdsSchema,
	includeRecentlyDeletedItems: accountSpaceIndexIncludeRecentlyDeletedItemsSchema,
	includePurposes: accountSpaceIndexIncludePurposesSchema,
	limit: accountSpaceIndexLimitSchema,
	scope: accountSpaceIndexScopeSchema.default("account"),
	selectedProfileId: trimmedNonEmptyStringSchema
});
const accountSpaceIndexJoinedProfileSchema = z$1.strictObject({
	lastJoinedAt: ISO_DATE_TIME_SCHEMA,
	pinnedAt: nullableIsoDateTimeSchema,
	profileId: trimmedNonEmptyStringSchema,
	profileName: trimmedNonEmptyStringSchema
});
const accountSpaceIndexItemSchema = z$1.strictObject({
	messagePreview: z$1.strictObject({
		signalId: trimmedNonEmptyStringSchema,
		profileId: trimmedNonEmptyStringSchema,
		timestamp: z$1.number().int().nonnegative(),
		text: z$1.string().max(240)
	}).nullable().optional(),
	memberPreviews: z$1.array(z$1.strictObject({
		profileId: trimmedNonEmptyStringSchema,
		profileName: trimmedNonEmptyStringSchema,
		profileKind: z$1.enum(["human", "agent"]),
		agentControllerRef: nullableTrimmedStringSchema
	})).max(3).optional(),
	creatorKind: z$1.union([profileSpaceCreatorKindSchema, z$1.null()]),
	creatorOwnerUserId: nullableTrimmedStringSchema,
	creatorProfileId: nullableTrimmedStringSchema,
	creatorProfileName: nullableTrimmedStringSchema,
	gatewayHost: profileSpaceHistoryGatewayHostSchema,
	joinedProfiles: z$1.array(accountSpaceIndexJoinedProfileSchema),
	lastJoinedAt: ISO_DATE_TIME_SCHEMA,
	lastSeenTs: nullableNonNegativeIntSchema,
	openProfileId: trimmedNonEmptyStringSchema,
	openProfileName: trimmedNonEmptyStringSchema,
	organizationId: trimmedNonEmptyStringSchema,
	pinnedAt: nullableIsoDateTimeSchema,
	pinnedProfileIds: z$1.array(trimmedNonEmptyStringSchema),
	spaceCreatedAt: nullableIsoDateTimeSchema,
	spaceId: trimmedNonEmptyStringSchema,
	spaceName: trimmedNonEmptyStringSchema,
	activeAgentCount: nullableNonNegativeIntSchema.optional(),
	activeHumanCount: nullableNonNegativeIntSchema.optional(),
	activeMemberCount: nullableNonNegativeIntSchema.optional(),
	directMessageConvertedAt: nullableIsoDateTimeSchema.optional(),
	directMessageProfileIds: z$1.union([z$1.tuple([trimmedNonEmptyStringSchema, trimmedNonEmptyStringSchema]), z$1.null()]).optional(),
	spacePurpose: nullableTrimmedStringSchema.optional(),
	spaceUpdatedAt: nullableIsoDateTimeSchema,
	status: trimmedNonEmptyStringSchema,
	updatedAt: ISO_DATE_TIME_SCHEMA,
	visibility: spaceOrganizationVisibilitySchema
});
const accountSpaceIndexDeletedItemSchema = z$1.strictObject({
	deletedAt: ISO_DATE_TIME_SCHEMA,
	deletedByProfileName: nullableTrimmedStringSchema,
	spaceId: trimmedNonEmptyStringSchema,
	spaceName: nullableTrimmedStringSchema
});
const accountSpaceIndexResponseSchema = z$1.strictObject({
	pinnedItems: z$1.array(accountSpaceIndexItemSchema),
	recentlyDeletedItems: z$1.array(accountSpaceIndexDeletedItemSchema).optional(),
	spaceItems: z$1.array(accountSpaceIndexItemSchema)
});
//#endregion
export { accountSpaceIndexDeletedItemSchema, accountSpaceIndexIncludeDirectMessageProfileIdsSchema, accountSpaceIndexIncludePurposesSchema, accountSpaceIndexIncludeRecentlyDeletedItemsSchema, accountSpaceIndexItemSchema, accountSpaceIndexJoinedProfileSchema, accountSpaceIndexLimitSchema, accountSpaceIndexQuerySchema, accountSpaceIndexResponseSchema, accountSpaceIndexScopeSchema };

//# sourceMappingURL=account-space-index.js.map